import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { ActionType } from 'src/app/core/enums/action-type.enum';
import { ReceiptVoucherMST } from 'src/app/core/interfaces/resepit-voucher.interface';

export interface VoucherTab {
  id: string;
  title: string;
  voucher: ReceiptVoucherMST | null;
  actionType: ActionType;
  isActive: boolean;
  formData?: any;
  isEditing?: boolean;
}

@Component({
  selector: 'app-voucher-tabs',
  templateUrl: './voucher-tabs.component.html',
  styleUrl: './voucher-tabs.component.scss',
})
export class VoucherTabsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() tabs: VoucherTab[] = [];
  @Output() tabChange = new EventEmitter<string>();
  @Output() tabClose = new EventEmitter<string>();
  @Output() tabAdd = new EventEmitter<void>();
  @Output() tabRename = new EventEmitter<{ id: string; newTitle: string }>();
  @Output() activeTabChange = new EventEmitter<VoucherTab>();

  activeTabId: string | null = null;
  editingTabId: string | null = null;
  editInputValue: string = '';
  private destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private localizationService: LocalizationService
  ) {}

  get isRtl(): boolean {
    return this.localizationService.getCachedLanguage() === 'ar';
  }

  ngOnInit(): void {
    this.initializeActiveTab();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabs'] && !changes['tabs'].firstChange) {
      const previousTabs = changes['tabs'].previousValue || [];
      const currentTabs = changes['tabs'].currentValue || [];

      // If tabs array changed, validate and update state
      if (currentTabs.length !== previousTabs.length) {
        this.validateTabsState();
      }

      // If active tab was removed, switch to another tab
      if (this.activeTabId) {
        const activeTabExists = currentTabs.some(
          (t: VoucherTab) => t.id === this.activeTabId
        );
        if (!activeTabExists && currentTabs.length > 0) {
          const newActiveTab =
            currentTabs.find((t: VoucherTab) => t.isActive) || currentTabs[0];
          this.setActiveTab(newActiveTab.id);
        }
      } else {
        this.initializeActiveTab();
      }
    }
  }

  private initializeActiveTab(): void {
    if (this.tabs.length > 0 && !this.activeTabId) {
      const activeTab = this.tabs.find((t) => t.isActive) || this.tabs[0];
      if (activeTab) {
        this.setActiveTab(activeTab.id);
      }
    }
  }

  private validateTabsState(): void {
    // Ensure at least one tab is active
    const hasActiveTab = this.tabs.some((t) => t.isActive);
    if (!hasActiveTab && this.tabs.length > 0) {
      this.tabs[0].isActive = true;
      this.activeTabId = this.tabs[0].id;
    }

    // Ensure editing state is consistent
    this.tabs.forEach((tab) => {
      if (tab.isEditing && tab.id !== this.editingTabId) {
        tab.isEditing = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setActiveTab(tabId: string): void {
    // Validate tab exists
    const tab = this.tabs.find((t) => t.id === tabId);
    if (!tab) {
      console.warn(`Tab with id ${tabId} not found`);
      return;
    }

    // Exit editing mode if switching tabs
    if (this.editingTabId && this.editingTabId !== tabId) {
      this.finishEditingTab(this.editingTabId, false);
    }

    this.activeTabId = tabId;
    this.tabs.forEach((t) => {
      t.isActive = t.id === tabId;
    });

    this.tabChange.emit(tabId);
    this.activeTabChange.emit(tab);
    this.cdr.detectChanges();
  }

  closeTab(tabId: string, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    // Validate tab exists
    const tabIndex = this.tabs.findIndex((t) => t.id === tabId);
    if (tabIndex === -1) {
      console.warn(`Tab with id ${tabId} not found`);
      return;
    }

    // Don't allow closing the last tab
    if (this.tabs.length <= 1) {
      console.warn('Cannot close the last tab');
      return;
    }

    // Exit editing mode if closing the tab being edited
    if (this.editingTabId === tabId) {
      this.finishEditingTab(tabId, false);
    }

    // If closing the active tab, switch to another tab first
    if (this.tabs[tabIndex].isActive) {
      const newActiveIndex = tabIndex > 0 ? tabIndex - 1 : tabIndex + 1;
      if (this.tabs[newActiveIndex]) {
        this.setActiveTab(this.tabs[newActiveIndex].id);
      }
    }

    this.tabClose.emit(tabId);
  }

  addTab(): void {
    this.tabAdd.emit();
  }

  onTabRightClick(tabId: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.startEditingTab(tabId);
  }

  startEditingTab(tabId: string): void {
    const tab = this.tabs.find((t) => t.id === tabId);
    if (!tab) {
      console.warn(`Tab with id ${tabId} not found`);
      return;
    }

    // Exit editing mode for any other tab
    if (this.editingTabId && this.editingTabId !== tabId) {
      this.finishEditingTab(this.editingTabId, false);
    }

    this.editingTabId = tabId;
    this.editInputValue = tab.title;
    tab.isEditing = true;
    this.cdr.detectChanges();

    // Focus the input after a short delay to ensure it's rendered
    setTimeout(() => {
      const input = document.getElementById(
        `tab-edit-${tabId}`
      ) as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  finishEditingTab(tabId: string, save: boolean = true): void {
    const tab = this.tabs.find((t) => t.id === tabId);
    if (!tab) {
      console.warn(`Tab with id ${tabId} not found`);
      return;
    }

    if (save && this.editInputValue.trim()) {
      const newTitle = this.editInputValue.trim();
      // Validate title is not empty and not duplicate (optional validation)
      if (newTitle.length > 0) {
        tab.title = newTitle;
        this.tabRename.emit({ id: tabId, newTitle });
      } else {
        // If empty, restore original title
        this.editInputValue = tab.title;
      }
    }

    tab.isEditing = false;
    this.editingTabId = null;
    this.editInputValue = '';
    this.cdr.detectChanges();
  }

  onEditKeyDown(event: KeyboardEvent, tabId: string): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.finishEditingTab(tabId, true);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.finishEditingTab(tabId, false);
    }
  }

  getActiveTab(): VoucherTab | undefined {
    return this.tabs.find((t) => t.id === this.activeTabId);
  }
}
